import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TreeModule } from 'primeng/tree';
import { BehaviorSubject, combineLatest, map, Observable, of } from 'rxjs';
import { DestinationFilter, PriceList } from '../../models';
import { Company, CompanyEnum } from '../../models/company.model';
import { ApplicationFormService } from '../../services/application-form.service';
import { FilterService } from '../../services/filter.service';
import { SharedDataStore } from '../../store';
import { PriceListPanelComponent } from '../price-list-panel/price-list-panel.component';
import { PriceListsDataComponent } from '../price-lists-data/price-lists-data.component';

@Component({
  standalone: true,
  selector: 'lx-price-lists-portal-price-lists',
  imports: [CommonModule, ProgressSpinnerModule, TreeModule, PriceListsDataComponent, PriceListPanelComponent],
  templateUrl: './price-lists.component.html',
  styleUrls: ['./price-lists.component.scss'],
})
export class PriceListsComponent implements OnInit, OnChanges {
  @Input() company: Company = CompanyEnum.KIL;

  filteredPriceLists$: Observable<PriceList[]>;
  applicationForms = [];

  priceListsFromStore$: Observable<PriceList[]> | undefined;
  priceListsFiltersFromStore$: Observable<DestinationFilter[]> | undefined;

  private filtersSubject = new BehaviorSubject<DestinationFilter[]>([]);
  private selectedFilters$ = this.filtersSubject.asObservable();

  private readonly sharedDataStore = inject(SharedDataStore);
  private readonly applicationFormService = inject(ApplicationFormService);
  private readonly filterService = inject(FilterService);

  ngOnInit(): void {
    this.priceListsFromStore$ = this.sharedDataStore.priceListsForCompany(this.company).pipe(map((priceLists) => priceLists || []));
    this.priceListsFiltersFromStore$ = this.priceListsFromStore$.pipe(map((priceLists) => this.filterService.getSchoolPriceListFilters(priceLists) || []));
    this.initializeFilteredPriceLists();
  }

  ngOnChanges({ company }: SimpleChanges): void {
    if (company?.currentValue !== company?.previousValue) {
      this.applicationForms = this.applicationFormService.getApplicationForms(company?.currentValue);
    }
  }

  filterSchools(filters: DestinationFilter | DestinationFilter[]) {
    this.filtersSubject.next(Array.isArray(filters) ? filters : [filters]);
  }

  private initializeFilteredPriceLists() {
    this.filteredPriceLists$ = combineLatest([this.priceListsFromStore$ || of([]), this.selectedFilters$]).pipe(
      map(([priceLists, selectedFilters]: [PriceList[], DestinationFilter[]]) => {
        if (!priceLists?.length) {
          return [];
        }

        if (!selectedFilters?.length) {
          return [...priceLists];
        }

        return priceLists.reduce((filteredPriceLists: PriceList[], priceList) => {
          const priceListInSelectedFilterIndex = (selectedFilters as DestinationFilter[]).findIndex(
            (selectedFilter: DestinationFilter) => selectedFilter.key === priceList.code,
          );
          if (priceListInSelectedFilterIndex >= 0) {
            return [...filteredPriceLists, priceList];
          }

          return [...filteredPriceLists];
        }, []);
      }),
    );
  }
}
